import React, { Component } from 'react'
import ChartAxisDirection, { ChartZoomDirection } from './ChartAxisDirection'
import shortid from 'shortid'
import * as d3 from 'd3';
import MultiFormatDate from './ChartDateFormats'
import { xyzoom, xyzoomTransform } from "d3-xyzoom";
import cdx from './ChartDrawingContext'
import './ChartGraph.css'
import Transform from './Transform';
import TimeFrames, { TimeFramesData } from './ChartTimeFrames';

var sma = require('sma');

const getHiDPIRatio = (canvas) => {
    var devicePixelRatio = window.devicePixelRatio || 1
    var c = canvas.getContext('2d')
    var backingStoreRatio = [
        c.webkitBackingStorePixelRatio,
        c.mozBackingStorePixelRatio,
        c.msBackingStorePixelRatio,
        c.oBackingStorePixelRatio,
        c.backingStorePixelRatio,
        1
    ].reduce(function (a, b) { return a || b })

    return devicePixelRatio / backingStoreRatio
}

const CanvasLayers = () => {
    return {
        mapped: {},
        layers: [],
        add: function (identifier) {
            var layer = {
                identifier: identifier,
                canvas: null,
                context: null,
                drawables: {},
                drawableKeys: []
            }
            const index = this.layers.push(layer)
            this.mapped[identifier] = index - 1
        },
        layer: function (identifier) {
            return this.layers[this.mapped[identifier]]
        }
    }
}

const Drawable = (func, data) => {
    return (ctx, dtx, updateData = false) => {
        data = updateData ? updateData : data
        func(ctx, dtx, data)
    }
}

class ChartGraph extends Component {

    constructor(props) {
        super(props)

        this.container = React.createRef()
        this.mainCanvas = React.createRef()
        this.mouseCanvas = React.createRef()

        this.zoomID = this.props.zoomID
        this.shouldBroadcastZoom = true

        this.customBase = document.createElement('datastore');
        this.dataStore = d3.select(this.customBase)

        this.isAxis = this.props.isAxis

        this.canvasLayers = CanvasLayers()
        this.canvasLayers.add("main")
        this.canvasLayers.add("mouse")

        this.scaleIsLocked = false

        const shouldRender = {
            asYAxis: (this.isAxis && this.props.axisDirection === ChartAxisDirection.VERTICAL),
            xTicks: (this.isAxis && this.props.axisDirection === ChartAxisDirection.HORIZONTAL) || (this.props.zoomDirection === ChartZoomDirection.XY),
            yTicks: (this.isAxis && this.props.axisDirection === ChartAxisDirection.VERTICAL) || (this.props.zoomDirection === ChartZoomDirection.XY),
            mouse: this.props.zoomDirection === ChartZoomDirection.XY
        }

        this.mouseData = { active: false, x: 0, y: 0 }
        this.addToChartLayerIfValid(this.canvasLayers.layer("main"), shouldRender.yTicks, Drawable(cdx.yTicks, { tickCount: 5, asAxis: this.isAxis }), true, false)
        this.addToChartLayerIfValid(this.canvasLayers.layer("main"), shouldRender.xTicks, Drawable(cdx.xTicks, { tickCount: 5, asAxis: this.isAxis }), true, false)
        this.addToChartLayerIfValid(this.canvasLayers.layer("mouse"), shouldRender.mouse, Drawable(cdx.mouseCross, this.mouseData), true, false)
        this.addToChartLayerIfValid(this.canvasLayers.layer("main"), shouldRender.asYAxis, Drawable(cdx.markers, { markers: [{ v: 60000 }] }), true, false)


        this.transform = new Transform(0, 0, 1, 1)

        const customBase = document.createElement('custom');
        this.custom = d3.select(customBase);
    }

    broadcastingZoom = {
        start: () => { this.shouldBroadcastZoom = true; },
        stop: () => { this.shouldBroadcastZoom = false; }
    }

    getZoomTransform = () => {
        return xyzoomTransform(d3.select(this.mainCanvas.current).node())
    }

    handleToggleLockScaleRatio = (locked) => {

    }

    addToChartLayerIfValid = (layer, validate, ...rest) => {
        var condition = typeof validate === "function" ? validate() : validate
        if (condition) {
            this.addToChartLayer(layer, ...rest)
        }
    }

    addToChartLayer = (layer, func, visible, asSelectable = false) => {
        const id = shortid.generate()
        layer.drawables[id] = { draw: func, sel: asSelectable, visible: visible }
        layer.drawableKeys.push(id)
    }

    onZoom = (event) => {
        if (!this.shouldBroadcastZoom) {
            return
        }
        this.props.onZoom(event, this.zoomID, this.props.rowID, this.props.zoomDirection)
    }

    getZoomTransform = () => {
        return xyzoomTransform(d3.select(this.mainCanvas.current).node())
    }

    setZoomTransform = (transform) => {

        this.broadcastingZoom.stop()

        d3.select(this.mainCanvas.current).call(this.zoom.transform, transform);

        this.transform = xyzoomTransform(d3.select(this.mainCanvas.current).node())

        this.broadcastingZoom.start()

        return this.transform
    }

    handleZoom = (transform, zoomID, rowID, sourceZoomDirection) => {

    }

    update = () => {
        this.draw(["main"])
    }

    draw = (layerKeys) => {
        const transform = xyzoomTransform(d3.select(this.mainCanvas.current).node()) //this.transform//xyzoomTransform(d3.select(this.mainCanvas.current).node())
        const { width, height } = this.calculateBoundingSize()

        const y = transform.rescaleY(this.y)
        const x = transform.rescaleX(this.x)

        //

        this.dtx = {
            timeFrame: this.timeFrame,
            container: this.container.current,
            formatTime: MultiFormatDate,
            width: width,
            height: height,
            scales: {
                x: x,
                y: y,
            }
        }

        layerKeys.forEach(layerKey => {
            this.drawLayer(this.canvasLayers.layer(layerKey), this.dtx)
        })
    }

    drawLayer = (layer, dtx) => {
        const context = layer.context
        context.save();
        context.scale(this.pixelRatio, this.pixelRatio)
        context.clearRect(0, 0, dtx.width + 2, dtx.height + 2)


        layer.drawableKeys.forEach((key) => {
            context.beginPath()
            layer.drawables[key].draw(context, dtx)
            context.stroke()
        });

        context.restore();
    }

    setTimeFrame = (key) => {
        this.timeFrame = TimeFrames(key)
        this.update()
    }

    databind = (data) => {
        const reversed = data.reverse()
        this.ohlc = TimeFramesData(reversed)

        for (const [key, value] of Object.entries(this.ohlc)) {
            const closes = []
            this.ohlc[key].ohlc.forEach((ohlc) => {
                closes.push(ohlc.Close)
            })

            const lengths = [20, 30, 40, 50, 60, 70]
            
            this.ohlc[key].averages = []
            
            lengths.forEach((avgLength) => {
                const series = []
                const average = sma(closes, avgLength)
                for(let i = 0; i < average.length; i++) {
                    series.push({ v: average[i], date: this.ohlc[key].ohlc[i + (avgLength - 1)].date })
                } 
                this.ohlc[key].averages.push(series)
            })
            
            //
        }

        /*
        for (const [key, value] of Object.entries(this.ohlc)) {
            const closes = []
            this.ohlc[key].ohlc.forEach((ohlc) => {
                closes.push(ohlc.Close)
            })
            this.ohlc[key].average = sma(closes, 10)
            this.ohlc[key].average2 = sma(closes, 11)
        }

        console.log("lenghts")
        console.log(this.ohlc["1W"].ohlc.length - this.ohlc["1W"].average.length)
        console.log(this.ohlc["1W"].ohlc.length - this.ohlc["1W"].average2.length)
        console.log(this.ohlc["1W"].ohlc.length)
        */
        
        const getData = (timeframeKey) => { return this.ohlc[timeframeKey] }
        this.addToChartLayerIfValid(this.canvasLayers.layer("main"), true, Drawable(cdx.ohlc, { getData: getData }), true, false)
        this.update()
    }

    componentWillUnmount = () => {
        clearInterval(this.updateTimeInterval)
    }

    resetRange = (width, height) => {
        this.x.range([0, width]);
        this.y.range([height, 0]);
    }

    resetZoomExtents = () => {
        this.zoom.extent(this.getZoomExtent()).translateExtent(this.getZoomTranslateExtent());
    }

    getZoomExtent = () => {
        return [[this.x.range()[0], this.y.range()[1]], [this.x.range()[1], this.y.range()[0]]]
    }

    getZoomTranslateExtent = () => {
        return [[this.x.range()[0], this.y.range()[1]], [this.x.range()[1], this.y.range()[0]]]
    }

    getZoomScaleExtent = () => {
        return [[1, 100000], [1, 100000]]
        //const { width } = this.calculateBoundingSize()
        //const hourCount = (this.chartData.domainX[1] - this.chartData.domainX[0]) / (1000 * 3600)
        //const maxHoursPerWidth = width / 3
        //const minScale = Math.ceil(hourCount / maxHoursPerWidth)
        //return [[minScale, 100000], [minScale, 100000]]
    }

    componentDidMount = () => {

        this.timeFrame = TimeFrames("1h")

        this.chartData = this.props.chartData
        const renderProps = this.props.chartRenderProps

        this.pixelRatio = getHiDPIRatio(this.mainCanvas.current)

        const mainLayer = this.canvasLayers.layer("main")
        mainLayer.context = this.mainCanvas.current.getContext('2d')
        mainLayer.canvas = this.mainCanvas.current

        const mouseLayer = this.canvasLayers.layer("mouse")
        mouseLayer.context = this.mouseCanvas.current.getContext('2d')
        mouseLayer.canvas = this.mouseCanvas.current

        this.x = renderProps.scaleFuncX().domain(this.chartData.domainX);
        this.y = renderProps.scaleFuncY().domain(this.chartData.domainY);

        const { width, height } = this.calculateBoundingSize()
        this.resetRange(width, height)

        this.zoom = xyzoom().extent(this.getZoomExtent()).scaleExtent(this.getZoomScaleExtent()).scaleRatio([1, 1]).translateExtent(this.getZoomTranslateExtent())
            .on('zoom', () => {
                if ((d3.event.sourceEvent && d3.event.sourceEvent.type === 'zoom')) {
                    return
                }
                this.onZoom(d3.event)
            });

        this.resetZoomExtents()
        this.resizeCanvas()
        d3.select(this.mainCanvas.current).call(this.zoom)
        this.draw(["main"])

        /* update domain to include current time.. */
        this.updateTimeInterval = setInterval(() => {
            this.updateTime();
        }, 1000);

        d3.select(mainLayer.canvas).on('mousemove', () => {
            this.mouseData.active = true
            this.mouseData.x = d3.event.layerX || d3.event.offsetX
            this.mouseData.y = d3.event.layerY || d3.event.offsety
            this.draw(["mouse"])
        })

        d3.select(mainLayer.canvas).on("mouseout", () => {
            this.mouseData.active = false
            this.draw(["mouse"])
        });

    }

    updateTime = () => {
        /*const mindate = new Date(2018,4,1)
        const maxdate = new Date()
        this.x.domain([mindate, maxdate])
        this.zoom
            .extent([[this.x.range()[0], this.y.range()[0]], [this.x.range()[1], this.y.range()[1]]])
            .translateExtent([[this.x.range()[0], this.y.range()[0]], [this.x.range()[1], this.y(this.ymax)]]);*/
    }

    calculateBoundingSize = () => {
        const size = this.container.current.getBoundingClientRect()
        return { width: Math.round(size.width), height: Math.round(size.height) }
    }

    handleResize = () => {
        this.broadcastingZoom.stop()

        const oldTransform = xyzoomTransform(d3.select(this.mainCanvas.current).node())
        const { width, height } = this.calculateBoundingSize()

        const oldWidth = this.x.range()[1]
        const oldHeight = this.y.range()[0]
        const oldFullHeight = oldHeight * oldTransform.ky
        const oldFullWidth = oldWidth * oldTransform.kx
        const relativeOldY = oldTransform.y / oldFullHeight
        const relativeOldX = oldTransform.x / oldFullWidth

        this.resetRange(width, height)

        const newHeight = height
        const newWidth = width

        const newFullHeight = newHeight * oldTransform.ky
        const newFullWidth = newWidth * oldTransform.kx

        let newY = newFullHeight * relativeOldY
        let newX = newFullWidth * relativeOldX

        const heightRatio = oldHeight / height
        let adjustedNewYRatio = oldTransform.ky * heightRatio

        const widthRatio = oldWidth / width
        let adjustedNewXRatio = oldTransform.kx * widthRatio

        adjustedNewYRatio = Math.max(1, adjustedNewYRatio)
        adjustedNewXRatio = Math.max(1, adjustedNewXRatio)

        newY = newY * heightRatio
        newX = newX * widthRatio

        const newTransform = new Transform(newX, newY, adjustedNewXRatio, adjustedNewYRatio)

        this.setZoomTransform(newTransform)
        this.resetZoomExtents()
        this.resizeCanvas()
        this.draw(["main"])
        this.broadcastingZoom.start()

        return this.getZoomTransform()
    }

    resizeCanvas = () => {
        const { width, height } = this.calculateBoundingSize()
        const ow = width
        const oh = height
        const ow2 = width * this.pixelRatio
        const oh2 = height * this.pixelRatio
        d3.select(this.mainCanvas.current).attr('width', ow2).attr('height', oh2).style('width', ow + 'px').style('height', oh + 'px')
        d3.select(this.mouseCanvas.current).attr('width', ow2).attr('height', oh2).style('width', ow + 'px').style('height', oh + 'px')
    }

    render() {
        let sizeStyle = this.props.preferedSize ? { flex: "0 0 " + this.props.preferedSize + "px" } : {}
        if (this.isAxis && this.props.zoomDirection === ChartZoomDirection.X) {
            sizeStyle = { flex: "1 1 " + this.props.preferedSize + "px" }
        }
        return (
            <div ref={this.container} className="chart-graph-container" style={sizeStyle} >
                <canvas data-zid={this.props.zoomID} className="canvas-main" ref={this.mainCanvas} />
                <canvas className="canvas-mouse" ref={this.mouseCanvas} />
            </div>
        )
    }
}

ChartGraph.defaultProps = {
    isAxis: false
}


export default ChartGraph;

function withTestGraph(Component) {

    class ChartGraphTest extends React.Component {
        render() {
            const { forwardedRef, ...rest } = this.props;
            return <Component drawables={[]} ref={forwardedRef} {...rest} />
        }

    }
    return React.forwardRef((props, ref) => {
        return <ChartGraphTest {...props} forwardedRef={ref} />;
    });
}

function withAxisDirection(Component, axisDirection) {

    class ChartGraphAsAxis extends React.Component {
        render() {
            const { forwardedRef, ...rest } = this.props;
            return <Component isAxis={true} axisDirection={axisDirection} drawables={[]} ref={forwardedRef} {...rest} />
        }

    }
    return React.forwardRef((props, ref) => {
        return <ChartGraphAsAxis {...props} forwardedRef={ref} />;
    });
}

const ChartTestGraph = withTestGraph(ChartGraph)

const ChartGraphVerticalAxis = withAxisDirection(ChartGraph, ChartAxisDirection.VERTICAL)

const ChartGraphHorizontalAxis = withAxisDirection(ChartGraph, ChartAxisDirection.HORIZONTAL)

export { ChartGraphVerticalAxis, ChartGraphHorizontalAxis, ChartTestGraph };


// WEBPACK FOOTER //
// ./src/ChartGraph.js